/**
 * PWA registration — service worker + install prompt handling.
 */

export function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) return;
  if (typeof window === 'undefined') return;
  // Skip in dev (Vite serves /sw.js with no-cache headers, registration
  // in dev is just noise)
  if ((import.meta as any).env?.DEV) return;

  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js')
      .then((reg) => {
        // eslint-disable-next-line no-console
        console.log('[pwa] sw registered, scope:', reg.scope);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.warn('[pwa] sw registration failed:', err);
      });
  });
}

export interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

let deferredPrompt: InstallPromptEvent | null = null;
const listeners = new Set<(canInstall: boolean) => void>();

export function setupInstallPrompt() {
  if (typeof window === 'undefined') return;

  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredPrompt = e as InstallPromptEvent;
    listeners.forEach((fn) => fn(true));
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    listeners.forEach((fn) => fn(false));
  });
}

export function onInstallAvailable(fn: (canInstall: boolean) => void): () => void {
  listeners.add(fn);
  // Fire immediately if we already have a deferred prompt
  if (deferredPrompt) fn(true);
  return () => listeners.delete(fn);
}

export async function triggerInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!deferredPrompt) return 'unavailable';
  await deferredPrompt.prompt();
  const choice = await deferredPrompt.userChoice;
  deferredPrompt = null;
  listeners.forEach((fn) => fn(false));
  return choice.outcome;
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone === true
  );
}
